import { Fragment, useContext, useEffect, useState } from "react";
import { BaseModal } from "../../../../components/modal";
import {
  PaymentModalWrapper,
  ModalHeader,
  CloseButton,
  FormGrid,
  FormGroup,
  FormLabel,
  FormSelect,
  FormInput,
  AmountInputWrapper,
  PaymentCard,
  PaymentCardLeft,
  RadioIndicator,
  PaymentMethodLabel,
  PaymentCardRight,
  SecureBadge,
  PaystackWordmark,
  ModalFooter,
  CancelButton,
  ContinueButton,
  ErrorText,
  SuccessBadge,
  Step2Header,
  DetailRow,
  TotalRow,
  StatusPill,
} from "./styled";
import { Context } from "../../../../context";
import { GreenTickII, PaystackLogo } from "../../../../assets";
import { getCompanies } from "../../../../utils/apis/company/getCompanies";
import Cookies from "universal-cookie";
import { purchaseRequestService } from "../../../../utils/apis/credit/purchaseRequest";
import { DotLoader } from "react-spinners";
import { PaystackButton } from "react-paystack";
import { verifyPurchaseService } from "../../../../utils/apis/credit/verfiyPurchase";
import { useNavigate } from "react-router-dom";
import { getUser } from "../../../../utils/apis/user/getUser";
import { getCompanyDetails } from "../../../../utils/apis/company/getCompanyDetails";
import { getAllEmployees } from "../../../../utils/apis/employee/getAllEmployees";
import { getPlanService } from "../../../../utils/apis/plansandpricing/getPlan";
import { faXmark, faLock } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export const PaymentModal = () => {
  const cookies = new Cookies();
  const TOKEN = cookies.get("TOKEN");
  const PK = process.env.REACT_APP_PAYSTACK_PK;

  const navigate = useNavigate();

  const { isPaymentFormModalOpen, setIsPaymentFormModalOpen } =
    useContext(Context);

  const [isPaymentSuccessModal, setIsPaymentSuccessModal] = useState(false);
  const [formDetails, setFormDetails] = useState({
    companyId: "",
    creditAmount: "",
  });
  const [companies, setCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatedInvoice, setGeneratedInvoice] = useState(null);
  const [loggedInUser, setLoggedInUser] = useState({});
  const [creditInfo, setCreditInfo] = useState({});

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await getCompanies(TOKEN);
        return setCompanies(response);
      } catch (error) {
        console.error(error);
      }
    };
    fetchCompanies();
  }, [TOKEN, isPaymentFormModalOpen]);

  useEffect(() => {
    getUser(TOKEN)
      .then((data) => {
        setLoggedInUser(data);
      })
      .catch((err) => {
        console.error(err);
      });
  }, [TOKEN]);

  useEffect(() => {
    if (!formDetails.companyId) return setCreditInfo({});
    const fetchCompanyCreditInfo = async () => {
      try {
        const response = await getCompanyDetails(TOKEN, formDetails.companyId);
        const planInformation = await getPlanService(
          TOKEN,
          response?.data?.planId,
        );
        const employees = await getAllEmployees(TOKEN, formDetails.companyId, {
          employeeName: "",
          departmentId: "",
          jobTitle: "",
        });
        return setCreditInfo({
          employeeCount: employees.length,
          planUpperLimit: planInformation?.data?.upperLimit,
          planLowerLimit: planInformation?.data?.lowerLimit,
          ...response?.data,
        });
      } catch (error) {
        console.error(error);
      }
    };
    fetchCompanyCreditInfo();
  }, [TOKEN, formDetails, isPaymentFormModalOpen]);

  const handlePaystackSuccessCredit = async (reference) => {
    const payload = { reference: String(reference?.reference) };
    try {
      const response = await verifyPurchaseService(TOKEN, payload);
      if (response?.status === "Success") {
        setIsPaymentFormModalOpen(false);
        await getCompanyDetails(TOKEN, formDetails.companyId);
        navigate("/dashboard");
      } else {
        console.error(
          "Server failed to verify purchase. Please contact support.",
        );
      }
    } catch (error) {
      console.error("Purchase verification failed:", error);
    }
  };

  const handlePaystackCloseAction = () => {};

  const config = {
    reference: new Date().getTime().toString(),
    email: loggedInUser?.email,
    amount: `${generatedInvoice?.totalCreditCost}00`,
    publicKey: PK,
    metadata: {
      invoiceId: generatedInvoice?.invoiceId,
      creditAmount: generatedInvoice?.creditAmount,
      companyName: generatedInvoice?.companyName,
      payrollPlanName: generatedInvoice?.payrollPlanName,
      costPerCredit: generatedInvoice?.costPerCredit,
      totalCreditCost: generatedInvoice?.totalCreditCost,
      dateInitiated: generatedInvoice?.dateInitiated,
    },
  };

  const componentProps = {
    ...config,
    text: "Proceed to Pay",
    onSuccess: (reference) => handlePaystackSuccessCredit(reference),
    onClose: handlePaystackCloseAction,
  };

  const handleIsPaystackPaymentModalOpen = (e) => {
    e.stopPropagation();
    return handleCloseModal();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      const response = await purchaseRequestService(TOKEN, formDetails);
      setGeneratedInvoice(response?.result?.data);
      if (response?.result?.status === "Success") {
        setIsLoading(false);
        setIsPaymentSuccessModal(true);
      } else {
        setIsLoading(false);
        setError("Server failed to raise request. Please contact support.");
        console.error(
          "Server failed to raise request. Please contact support.",
        );
      }
    } catch (error) {
      setIsLoading(false);
      setError(`Purchase request failed. ${error.message}`);
      console.error("Purchase request failed:", error);
    }
  };

  const handleCloseModal = () => {
    setIsPaymentSuccessModal(false);
    setGeneratedInvoice(null);
    setFormDetails({
      companyId: "",
      creditAmount: "",
    });
    setError(null);
    setIsPaymentFormModalOpen(false);
  };

  return (
    <BaseModal
      open={isPaymentFormModalOpen}
      onClose={handleCloseModal}
      className={"payment-modal"}
      width="480px"
    >
      <PaymentModalWrapper>
        {!isPaymentSuccessModal ? (
          <Fragment>
            <ModalHeader>
              <div className="pm-header-text">
                <h2 className="pm-title">Payment Information</h2>
                <p className="pm-subtitle">
                  Complete your payment details below
                </p>
              </div>
              <CloseButton onClick={handleCloseModal}>
                <FontAwesomeIcon icon={faXmark} />
              </CloseButton>
            </ModalHeader>

            <form onSubmit={handleSubmit}>
              <FormGrid>
                <FormGroup>
                  <FormLabel>Select Company</FormLabel>
                  <FormSelect
                    name="companyId"
                    value={formDetails.companyId}
                    onChange={handleChange}
                    required
                  >
                    <option value="" disabled>
                      Select company
                    </option>
                    {companies?.map((company, index) => (
                      <option key={index} value={company.companyId}>
                        {company.name}
                      </option>
                    ))}
                  </FormSelect>
                </FormGroup>

                <FormGroup>
                  <FormLabel>Amount</FormLabel>
                  <AmountInputWrapper>
                    <FormInput
                      className="pm-amount-input"
                      name="creditAmount"
                      type="number"
                      min="0"
                      placeholder="0"
                      value={formDetails.creditAmount}
                      onChange={handleChange}
                      required
                    />
                  </AmountInputWrapper>
                </FormGroup>
              </FormGrid>

              {creditInfo.payrollPlan && creditInfo.creditCostPerEmployee && (
                <Fragment>
                  <ErrorText style={{ marginTop: "12px" }}>
                    You may consider purchasing at least{" "}
                    {Number(creditInfo.creditCostPerEmployee) *
                      Number(creditInfo.employeeCount)}{" "}
                    credits to run full payroll for your{" "}
                    {creditInfo.employeeCount} staff.
                  </ErrorText>
                </Fragment>
              )}

              <PaymentCard style={{ marginTop: "20px" }}>
                <PaymentCardLeft>
                  <RadioIndicator />
                  <PaymentMethodLabel>
                    <span className="pm-method-name">Pay with Paystack</span>
                    <span className="pm-method-desc">
                      Secure online payment
                    </span>
                  </PaymentMethodLabel>
                </PaymentCardLeft>
                <PaymentCardRight>
                  <SecureBadge>
                    <FontAwesomeIcon icon={faLock} className="pm-secure-icon" />
                    <span className="pm-secure-text">Secure</span>
                  </SecureBadge>
                  <PaystackWordmark>
                    <PaystackLogo />
                  </PaystackWordmark>
                </PaymentCardRight>
              </PaymentCard>

              <ModalFooter>
                <CancelButton type="button" onClick={handleCloseModal}>
                  Cancel
                </CancelButton>
                <ContinueButton type="submit" disabled={isLoading}>
                  {isLoading ? (
                    <span className="pm-spinner">
                      <DotLoader size={18} color="#ffffff" />
                    </span>
                  ) : (
                    "Continue"
                  )}
                </ContinueButton>
              </ModalFooter>

              {error && (
                <ErrorText style={{ marginTop: "8px" }}>{error}</ErrorText>
              )}
            </form>
          </Fragment>
        ) : (
          <Fragment>
            <Step2Header>
              <SuccessBadge>
                <GreenTickII />
              </SuccessBadge>
              <h2 className="pm-step2-title">Confirm Details</h2>
            </Step2Header>

            <div>
              <DetailRow>
                <span className="pm-detail-label">Invoice Id</span>
                <span className="pm-detail-value">
                  {generatedInvoice?.invoiceId}
                </span>
              </DetailRow>
              <DetailRow>
                <span className="pm-detail-label">Payroll Plan Name</span>
                <span className="pm-detail-value">
                  {generatedInvoice?.payrollPlanName}
                </span>
              </DetailRow>
              <DetailRow>
                <span className="pm-detail-label">Wallet Amount</span>
                <span className="pm-detail-value">
                  {generatedInvoice?.creditAmount}
                </span>
              </DetailRow>
              <DetailRow>
                <span className="pm-detail-label">Cost per credit</span>
                <span className="pm-detail-value">
                  {generatedInvoice?.costPerCredit}
                </span>
              </DetailRow>
              <DetailRow>
                <span className="pm-detail-label">Date Initiated</span>
                <span className="pm-detail-value">
                  {generatedInvoice?.dateInitiated}
                </span>
              </DetailRow>
              <DetailRow>
                <span className="pm-detail-label">Invoice Status</span>
                <StatusPill>{generatedInvoice?.status}</StatusPill>
              </DetailRow>
            </div>

            <TotalRow>
              <span className="pm-total-label">Total credit cost</span>
              <span className="pm-total-value">
                {generatedInvoice?.totalCreditCost}
              </span>
            </TotalRow>

            <ModalFooter>
              <CancelButton onClick={handleCloseModal}>Cancel</CancelButton>
              <div
                className="pm-paystack-wrapper"
                onClick={handleIsPaystackPaymentModalOpen}
              >
                <PaystackButton
                  {...componentProps}
                  className="pm-paystack-button"
                />
              </div>
            </ModalFooter>
          </Fragment>
        )}
      </PaymentModalWrapper>
    </BaseModal>
  );
};
